import type { PetitionDetailsPageData } from '#src/app/pages/petitionDetails/pageContext';
import { SECTION_ID_MAPPING } from '#src/app/pages/petitionDetails/shared/constants';

import type { HeroActivityType, HeroResult } from './types';
import { parseDate } from './utils';

export type TranslateFn = (key: string, replacements?: Record<string, string | number>) => string;

type SpotlightNode =
	| PetitionDetailsPageData['petition']['spotlightEndorsementUpdate']['nodes'][number]
	| PetitionDetailsPageData['petition']['spotlightMediaSourceUpdate']['nodes'][number]
	| PetitionDetailsPageData['petition']['spotlightStarterUpdate']['nodes'][number];

export type ResolverContext = {
	data: PetitionDetailsPageData;
	translate: TranslateFn;
	spotlightNodes: readonly SpotlightNode[];
};

export type HeroResolver = (ctx: ResolverContext) => HeroResult | null;

function stripHtmlAndTrim(value: string): string {
	return value
		.replace(/<[^>]*>/g, ' ')
		.replace(/&nbsp;/gi, ' ')
		.replace(/\s+/g, ' ')
		.trim();
}

function hasMeaningfulStarterUpdateContent(update: { title: string; description: string }): boolean {
	const title = update.title.trim();
	const description = stripHtmlAndTrim(update.description);
	return title.length > 0 && description.length > 0;
}

export const heroResolvers: Record<HeroActivityType, HeroResolver> = {
	dmResponse: ({ data, translate }) => {
		// oxlint-disable-next-line typescript/no-unnecessary-condition -- dmResponsesConnection is a nullable GraphQL connection field (see tests)
		const nodes = data.petition.dmResponsesConnection?.nodes;

		// oxlint-disable-next-line typescript/no-unnecessary-condition -- see the comment above
		if (!nodes || nodes.length === 0) return null;

		const latestDmResponse = nodes[0];
		const dmName = latestDmResponse.decisionMaker.displayName;
		const timeAgo = latestDmResponse.createdAtRelativeLocalized;
		return {
			content: {
				badgeLabel: translate('corgi.petition_details.momentum_bar.spotlight.dm_response_badge'),
				text: translate('corgi.petition_details.momentum_bar.spotlight.dm_response_text', { name: dmName }),
				relativeTime: timeAgo,
				showIcon: false,
				sectionId: SECTION_ID_MAPPING.DECISION_MAKERS,
			},
			timestamp: parseDate(latestDmResponse.createdAt),
		};
	},

	endorsement: ({ translate, spotlightNodes }) => {
		const node = spotlightNodes.find((n) => n.__typename === 'EndorsementPetitionUpdate');
		if (!node) return null;

		return {
			content: {
				badgeLabel: translate('corgi.petition_details.momentum_bar.spotlight.endorsement_badge'),
				text: translate('corgi.petition_details.momentum_bar.spotlight.endorsement_text_with_time', {
					name: node.title,
				}),
				relativeTime: node.createdAtRelativeLocalized,
				showIcon: false,
				sectionId: SECTION_ID_MAPPING.ENDORSEMENTS,
			},
			timestamp: parseDate(node.createdAt),
		};
	},

	mediaMention: ({ translate, spotlightNodes }) => {
		const mediaSource = spotlightNodes.find((n) => n.__typename === 'MediaSourcePetitionUpdate');
		if (!mediaSource) return null;

		return {
			content: {
				badgeLabel: translate('corgi.petition_details.momentum_bar.spotlight.media_mention_badge'),
				text: mediaSource.title
					? translate('corgi.petition_details.momentum_bar.spotlight.media_mention_text', {
							outletName: mediaSource.title,
						})
					: translate('corgi.petition_details.momentum_bar.spotlight.media_mention_text_no_outlet'),
				relativeTime: mediaSource.createdAtRelativeLocalized,
				showIcon: false,
				sectionId: SECTION_ID_MAPPING.MEDIA_MENTIONS,
			},
			timestamp: parseDate(mediaSource.createdAt),
		};
	},

	starterUpdate: ({ translate, spotlightNodes }) => {
		type StarterNode = Extract<(typeof spotlightNodes)[number], { __typename: 'StarterPetitionUpdate' }>;
		const node = spotlightNodes.find((n): n is StarterNode => {
			if (n.__typename !== 'StarterPetitionUpdate') return false;
			return hasMeaningfulStarterUpdateContent({ title: n.title, description: n.description });
		});
		if (!node) return null;

		return {
			content: {
				badgeLabel: translate('corgi.petition_details.momentum_bar.spotlight.starter_update_badge'),
				text: translate('corgi.petition_details.momentum_bar.spotlight.starter_update_text'),
				relativeTime: node.createdAtRelativeLocalized,
				showIcon: false,
				sectionId: SECTION_ID_MAPPING.UPDATES,
			},
			timestamp: parseDate(node.createdAt),
		};
	},
};
